import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MessageService, ConfirmationService } from 'primeng/api';
import { CotacaoService } from '../../services/cotacao/cotacao.service';
import { CotacaoDialogService } from '../../services/cotacao/cotacao-dialog.service';
import { EmpresaConfigService } from '../../services/configuracoes/empresa.service';
import { CotacaoDTO } from '../../models/gerenciar-contas/cotacao.model';
import { Moeda, MOEDA_CONFIG } from '../../enums/moeda.enum';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TableColumn } from '../../models/shared/table-column.model';
import { toLocalISOString } from '../../shared/utils/date-utils';
import { CampoCotacao, camposCotacaoVisiveis } from '../../utils/moeda-pareamento.util';

@Component({
  selector: 'app-cotacao',
  templateUrl: './cotacao.component.html',
  styleUrls: ['./cotacao.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class CotacaoComponent implements OnInit, OnDestroy {
  /**
   * Fallback hardcoded de ARS->PYG quando a busca externa (cambioschaco) falha
   * ou nao retorna ARS. Valor aproximado historico de cotacao 1 ARS ≈ 7,85 PYG
   * para permitir que o formulario funcione mesmo offline / fora do horario
   * comercial. Quando houver cotacao atualizada, ela sobrescreve este valor.
   */
  private static readonly PESO_GUARANI_FALLBACK = 7.85;

  // Controle de abas (0 = Cadastro, 1 = Historico)
  activeTabIndex: number = 1;

  form: CotacaoDTO = this.createEmptyForm();
  cotacoes: CotacaoDTO[] = [];
  cotacaoCarregando = false;
  submitting = false;
  cotacaoAtual: CotacaoDTO | null = null;
  cotacaoAtualCarregando = false;
  loading = false;

  /** Moeda padrao da empresa logada (determina quais pares de cotacao aparecem na tela). */
  private moedaEmpresa: Moeda | null = null;
  /** Campos de cotacao visiveis na UI/card/submit (derivados da moeda da empresa). */
  camposVisiveis: CampoCotacao[] = camposCotacaoVisiveis(null);

  // Edicao
  idEdit?: number;

  // Objetos de traducao
  texts = {
    titulo: '',
    subtitulo: '',
    aba_cadastro: '',
    aba_historico: '',
    nova_cotacao: '',
    editar_cotacao: '',
    visualizar_cotacao: '',
    cotacao_atual: '',
    historico_de_cotacoes: '',
    nenhuma_cotacao: '',
    adicione_cotacao: '',
    carregando: '',
    nenhuma_cotacao_atual: '',
    carregando_cotacao: ''
  };

  labels = {
    id: '',
    data_emissao: '',
    moeda_padrao: '',
    dolar_guarani: '',
    real_guarani: '',
    dolar_real: '',
    peso_guarani: '',
    itens_por_pagina: ''
  };

  placeholders = {
    buscar: '',
    dolar_x_guarani: '',
    real_x_guarani: '',
    dolar_x_real: '',
    peso_x_guarani: ''
  };

  botoes = {
    guardar: '',
    cancelar: '',
    confirmar: '',
    atualizar: '',
    limpar: ''
  };

  tooltips = {
    editar: '',
    visualizar: ''
  };

  spans = {
    x: '',
    dolar_guarani: '',
    real_guarani: '',
    dolar_real: '',
    peso_guarani: ''
  };

  mensagens = {
    sucesso_salvar: '',
    erro_salvar: '',
    erro_carregar: '',
    campos_obrigatorios: '',
    sucesso_editar: '',
    erro_editar: ''
  };

  private langChangeSub!: Subscription;

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'cotacao-visible-columns';

  // Colunas alinhadas ao vendas-app (mobile):
  // mostra id, emissao, dolarGuarani, realGuarani, dolarReal.
  // A coluna moedaPadrao nao e exibida no mobile.
  columns: TableColumn[] = [
    { key: 'id', labelKey: 'cotacao.labels.id', visible: true, fixed: true, defaultVisible: true },
    { key: 'emissao', labelKey: 'cotacao.labels.data_emissao', visible: true, fixed: false, defaultVisible: true },
    { key: 'dolarGuarani', labelKey: 'cotacao.labels.dolar_guarani', visible: true, fixed: false, defaultVisible: true },
    { key: 'realGuarani', labelKey: 'cotacao.labels.real_guarani', visible: true, fixed: false, defaultVisible: true },
    { key: 'dolarReal', labelKey: 'cotacao.labels.dolar_real', visible: true, fixed: false, defaultVisible: true },
    { key: 'pesoGuarani', labelKey: 'cotacao.labels.peso_guarani', visible: true, fixed: false, defaultVisible: true },
  ];

  // Paginacao
  first = 0;
  rows = 10;
  totalRecords = 0;
  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  // Ordenacao (backend)
  lastfield: string = 'criadoEm';
  lastSort: 'asc' | 'desc' = 'asc';

  // Busca
  searchTerm = '';
  private searchSubject = new Subject<string>();
  private searchSub?: Subscription;

  /** Guarda o ultimo valor de "rows" processado pelo onRowsPerPageChange,
   *  para evitar chamadas duplicadas de loadCotacoes quando o (ngModelChange)
   *  do dropdown de itens por pagina dispara no bootstrap com o valor atual. */
  private _lastRows: number = 10;

  constructor(
    private cotacaoService: CotacaoService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private empresaService: EmpresaConfigService,
    private cotacaoDialogService: CotacaoDialogService,
  ) {}

  ngOnInit() {
    this.loadColumnPreferences();
    // loadCotacoes() e buscarCotacaoExterna() foram encadeadas em loadMoedaEmpresa()
    // para evitar race condition: ambas dependem de camposVisiveis (derivado da moeda
    // da empresa) e do filtro de colunas aplicado por aplicarFiltroColunasPorMoeda().
    this.loadMoedaEmpresa();
    this.loadCotacaoAtual();
    this.loadTranslations();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });

    // Busca com debounce para evitar requisicao a cada tecla digitada.
    this.searchSub = this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.first = 0;
        this.loadCotacoes();
      });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
    if (this.searchSub) {
      this.searchSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    const isHistoricoAtivo = this.activeTabIndex === 1;

    this.texts = {
      titulo: this.translate.instant('cotacao.texts.gerenciar_cotacoes'),
      subtitulo: this.translate.instant(
        isHistoricoAtivo ? 'cotacao.texts.subtitulo_historico' : 'cotacao.texts.subtitulo_cadastro'
      ),
      aba_cadastro: this.translate.instant('cotacao.texts.aba_cadastro'),
      aba_historico: this.translate.instant('cotacao.texts.aba_historico'),
      nova_cotacao: this.translate.instant('cotacao.texts.nova_cotacao'),
      editar_cotacao: this.translate.instant('cotacao.texts.editar_cotacao'),
      visualizar_cotacao: this.translate.instant('cotacao.texts.visualizar_cotacao'),
      cotacao_atual: this.translate.instant('cotacao.texts.cotacao_atual'),
      historico_de_cotacoes: this.translate.instant('cotacao.texts.historico_de_cotacoes'),
      nenhuma_cotacao: this.translate.instant('cotacao.texts.nenhuma_cotacao'),
      adicione_cotacao: this.translate.instant('cotacao.texts.adicione_cotacao'),
      carregando: this.translate.instant('cotacao.texts.carregando'),
      nenhuma_cotacao_atual: this.translate.instant('cotacao.texts.nenhuma_cotacao_atual'),
      carregando_cotacao: this.translate.instant('cotacao.texts.carregando_cotacao')
    };

    this.labels = {
      id: this.translate.instant('cotacao.labels.id'),
      data_emissao: this.translate.instant('cotacao.labels.data_emissao'),
      moeda_padrao: this.translate.instant('cotacao.labels.moeda_padrao'),
      dolar_guarani: this.translate.instant('cotacao.labels.dolar_guarani'),
      real_guarani: this.translate.instant('cotacao.labels.real_guarani'),
      dolar_real: this.translate.instant('cotacao.labels.dolar_real'),
      peso_guarani: this.translate.instant('cotacao.labels.peso_guarani'),
      itens_por_pagina: this.translate.instant('cotacao.labels.itens_por_pagina')
    };

    this.placeholders = {
      buscar: this.translate.instant('cotacao.placeholders.buscar'),
      dolar_x_guarani: this.translate.instant('cotacao.placeholders.dolar_x_guarani'),
      real_x_guarani: this.translate.instant('cotacao.placeholders.real_x_guarani'),
      dolar_x_real: this.translate.instant('cotacao.placeholders.dolar_x_real'),
      peso_x_guarani: this.translate.instant('cotacao.placeholders.peso_x_guarani')
    };

    this.botoes = {
      guardar: this.translate.instant('cotacao.botoes.guardar'),
      cancelar: this.translate.instant('cotacao.botoes.cancelar'),
      confirmar: this.translate.instant('cotacao.botoes.confirmar'),
      atualizar: this.translate.instant('cotacao.botoes.atualizar'),
      limpar: this.translate.instant('cotacao.botoes.limpar')
    };

    this.tooltips = {
      editar: this.translate.instant('cotacao.tooltips.editar'),
      visualizar: this.translate.instant('cotacao.tooltips.visualizar')
    };

    this.spans = {
      x: this.translate.instant('cotacao.spans.x'),
      dolar_guarani: this.translate.instant('cotacao.spans.dolar_guarani'),
      real_guarani: this.translate.instant('cotacao.spans.real_guarani'),
      dolar_real: this.translate.instant('cotacao.spans.dolar_real'),
      peso_guarani: this.translate.instant('cotacao.spans.peso_guarani')
    };

    this.mensagens = {
      sucesso_salvar: this.translate.instant('cotacao.mensagens.sucesso_salvar'),
      erro_salvar: this.translate.instant('cotacao.mensagens.erro_salvar'),
      erro_carregar: this.translate.instant('cotacao.mensagens.erro_carregar'),
      campos_obrigatorios: this.translate.instant('cotacao.mensagens.campos_obrigatorios'),
      sucesso_editar: this.translate.instant('cotacao.mensagens.sucesso_editar'),
      erro_editar: this.translate.instant('cotacao.mensagens.erro_editar')
    };
  }

  // =========================================================================
  // Moeda padrao da empresa (controla visibilidade dos pares de cotacao)
  // =========================================================================

  /**
   * Obtem a moeda padrao da empresa logada (GET /configuracaoEmpresa/sessao)
   * para determinar quais pares de cotacao devem ser exibidos e validados.
   * Em caso de falha, mantem o fallback (trio tradicional).
   */
  private loadMoedaEmpresa(): void {
    this.empresaService.getSessao().subscribe({
      next: (config: any) => {
        const enumStr = config?.moeda?.enum as string | undefined;
        this.moedaEmpresa = enumStr ? (enumStr as Moeda) : null;
        this.camposVisiveis = camposCotacaoVisiveis(this.moedaEmpresa);
        this.aplicarFiltroColunasPorMoeda();
        // Encadeado apos definir camposVisiveis: garante que loadCotacoes use
        // o filtro de colunas correto e que buscarCotacaoExterna preencha somente
        // os campos pertinentes a moeda da empresa (evita race condition).
        this.loadCotacoes();
        this.buscarCotacaoExterna();
      },
      error: () => {
        this.moedaEmpresa = null;
        this.camposVisiveis = camposCotacaoVisiveis(null);
        this.aplicarFiltroColunasPorMoeda();
        // Fallback (trio tradicional): mantem o encadeamento apos definir camposVisiveis.
        this.loadCotacoes();
        this.buscarCotacaoExterna();
      },
    });
  }

  /**
   * Oculta, no historico, as colunas de pares nao pertinentes a moeda padrao
   * atual da empresa (respeitando a regra dinamica). O seletor de colunas
   * continua permitindo ao usuario esconder colunas dentre as visiveis.
   */
  private aplicarFiltroColunasPorMoeda(): void {
    const keysDeCotacao: CampoCotacao[] = [
      'dolarGuarani', 'realGuarani', 'dolarReal', 'pesoGuarani',
    ];
    this.columns.forEach(col => {
      if (keysDeCotacao.includes(col.key as CampoCotacao)) {
        const visivelParaMoeda = this.camposVisiveis.includes(col.key as CampoCotacao);
        if (!visivelParaMoeda) {
          col.visible = false;
        }
      }
    });
    this.columns = [...this.columns];
  }

  /** Indica se um campo de cotacao deve ser exibido/validado/enviado. */
  isVisivel(campo: CampoCotacao): boolean {
    return this.camposVisiveis.includes(campo);
  }

  // =========================================================================
  // Navegacao entre abas
  // =========================================================================

  novaCotacao(): void {
    this.limparFormulario();
    this.buscarCotacaoExterna();
    this.activeTabIndex = 0;
  }

  onTabChange(event: { index: number }): void {
    // index 0 = Cadastro, index 1 = Historico
    if (event.index === 1) {
      this.texts.subtitulo = this.translate.instant('cotacao.texts.subtitulo_historico');
    } else {
      this.texts.subtitulo = this.translate.instant('cotacao.texts.subtitulo_cadastro');
    }
  }

  private limparFormulario(): void {
    this.form = this.createEmptyForm();
    this.idEdit = undefined;
  }

  visualizarCotacao(id: number): void {
    this.cotacaoService.getById(`/cotacao/${id}`).subscribe({
      next: (response) => {
        this.form = { ...response };
        this.idEdit = id;
        this.activeTabIndex = 0;
        this.cdr.detectChanges();
      },
    });
  }

  get moedaPadraoLabel(): string {
    const moeda = this.form.moedaPadrao;
    if (!moeda) return '';
    const moedaStr = (typeof moeda === 'object' ? (moeda as any).nome || (moeda as any).value : moeda) as string;
    const config = MOEDA_CONFIG[moedaStr as Moeda];
    return config ? config.label : moedaStr;
  }

  // =========================================================================
  // Colunas
  // =========================================================================

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferencias de colunas:', e);
    }
  }

  // =========================================================================
  // Busca
  // =========================================================================

  onSearchChange(value: string): void {
    this.searchTerm = value || '';
    // Backend aceita buscaGeral filtrando por id, codigoCotacao, cotacoes e data de emissao.
    this.searchSubject.next(this.searchTerm.trim());
  }

  // =========================================================================
  // Ordenacao
  // =========================================================================

  onSortChange(event: any): void {
    if (!event.field) return;
    const field = event.field;
    const order = event.order === 1 ? 'asc' : 'desc';
    if (this.lastfield === field && this.lastSort === order) return;
    this.lastfield = field;
    this.lastSort = order;
    this.first = 0;
    this.loadCotacoes();
  }

  // =========================================================================
  // Paginacao
  // =========================================================================

  onPageChange(event: any): void {
    this.first = event.first;
    this.rows = event.rows;
    this.loadCotacoes();
  }

  onRowsPerPageChange(): void {
    // Guard anti-reentrada: o (ngModelChange) do dropdown de itens por pagina
    // dispara no bootstrap do PrimeNG com o valor atual, gerando uma chamada
    // duplicada de loadCotacoes alem da feita em ngOnInit. Ignorar quando
    // "rows" nao mudou de valor.
    if (this.rows === this._lastRows) return;
    this._lastRows = this.rows;
    this.first = 0;
    this.loadCotacoes();
  }

  // =========================================================================
  // CRUD
  // =========================================================================

  loadCotacoes(): void {
    this.loading = true;
    const page = this.rows > 0 ? Math.floor(this.first / this.rows) : 0;
    const size = this.rows;
    const sort = `${this.lastfield},${this.lastSort}`;
    const params: any = { page, size, sort };

    // Religa o buscaGeral no backend quando o usuario digita na barra de busca.
    // Segue o padrao do ApiService.buildQueryParams: a chave 'filter' e concatenada
    // diretamente na query string.
    const termo = this.searchTerm.trim();
    if (termo) {
      params.filter = `buscaGeral=${encodeURIComponent(termo)}`;
    }

    this.cotacaoService.get('/cotacao', params).subscribe({
      next: (response) => {
        this.cotacoes = response.content;
        this.totalRecords = response.totalElements || 0;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro_servidor'),
        });
      },
    });
  }

  submit(): void {
    if (this.submitting) return;

    // Validacao dinamica: campos obrigatorios dependem da moeda da empresa.
    // - GUARANI/REAL/DOLAR: trio tradicional required.
    // - PESO: apenas pesoGuarani required (> 0).
    const trioFaltando = (this.isVisivel('dolarGuarani') && !this.form.dolarGuarani)
      || (this.isVisivel('realGuarani') && !this.form.realGuarani)
      || (this.isVisivel('dolarReal') && !this.form.dolarReal);
    const pesoRequiredFaltando = this.isVisivel('pesoGuarani')
      && (this.form.pesoGuarani == null
        || Number.isNaN(this.form.pesoGuarani)
        || this.form.pesoGuarani <= 0);
    if (trioFaltando || pesoRequiredFaltando) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.mensagens.campos_obrigatorios,
      });
      return;
    }

    // Cotacoes existentes nao podem ser editadas - apenas visualizadas.
    // Ao entrar em modo visualizacao (idEdit preenchido), o botao de submit
    // fica oculto no template, entao este metodo so e chamado para criacao.
    if (this.idEdit) {
      return;
    }

    this.submitting = true;
    // Moeda padrao do payload: usa a moeda da empresa se conhecida,
    // caindo para GUARANI por backcompat (comportamento historico).
    const body: CotacaoDTO = {
      moedaPadrao: (this.moedaEmpresa ?? 'GUARANI') as any,
      emissao: toLocalISOString(),
    };
    // Preenche TODOS os pares tradicionais (dolarGuarani, realGuarani,
    // dolarReal) sempre no payload — o backend exige nao-nulos. Para
    // empresas cuja moeda padrao nao exibe esses campos (ex.: PESO), o
    // valor vem da ultima cotacao carregada (cotacaoAtual) ou do pre-
    // preenchimento externo (cambioschaco ja gravado em this.form pelo
    // buscarCotacaoExterna). Em ultimo caso, fallback seguro = 1.
    const dolarGuarani = this.resolverCampoObrigatorio('dolarGuarani');
    const realGuarani  = this.resolverCampoObrigatorio('realGuarani');
    const dolarReal    = this.resolverCampoObrigatorio('dolarReal');
    body.dolarGuarani = dolarGuarani;
    body.realGuarani  = realGuarani;
    body.dolarReal    = dolarReal;
    // pesoGuarani continua opcional no backend: so envia quando visivel e > 0.
    if (
      this.isVisivel('pesoGuarani')
      && this.form.pesoGuarani != null
      && this.form.pesoGuarani > 0
    ) {
      body.pesoGuarani = this.form.pesoGuarani;
    }

    this.cotacaoService.post('/cotacao', body).subscribe({
      next: () => {
        this.submitting = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.mensagens.sucesso_salvar,
        });
        // Invalida o cache da ultima cotacao e notifica consumidores abertos
        // (PDV, dialogos) para que passem a usar a cotacao recem-cadastrada
        // sem exigir reload da aplicacao.
        this.cotacaoService.clearCotacaoCache();
        this.cotacaoDialogService.notificarCotacaoSalva({
          dolarGuarani,
          realGuarani,
          dolarReal,
          pesoGuarani: body.pesoGuarani ?? null,
        });
        this.limparFormulario();
        this.loadCotacoes();
        this.loadCotacaoAtual();
        this.activeTabIndex = 1;
      },
      error: (err) => {
        this.submitting = false;
        const codigo: string | undefined = err?.error?.code;
        const detalheBackend: string | undefined = err?.error?.message;
        const chaveI18n = codigo ? `erros.${codigo}` : '';
        const traduzida = chaveI18n ? this.translate.instant(chaveI18n) : '';
        const detalhe = (traduzida && traduzida !== chaveI18n)
          ? traduzida
          : (detalheBackend || this.mensagens.erro_salvar);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: detalhe,
        });
      },
    });
  }

  // Cotacoes cadastradas nao podem ser excluidas - apenas visualizadas.
  // Comportamento espelha o Flutter (vendas-app/cotacao_list_screen.dart).

  /**
   * Resolve o valor de um campo tradicional de cotacao (dolarGuarani,
   * realGuarani ou dolarReal) para envio obrigatorio ao backend.
   *
   * Mesmo quando a moeda padrao da empresa nao exibe o campo (ex.: PESO
   * so ve pesoGuarani), o backend exige que os 3 pares tradicionais
   * nao sejam nulos. Este metodo escolhe o valor nesta ordem:
   *
   *   1. Valor atualmente no form (se > 0) — preenchido pelo usuario
   *      ou pelo pre-preenchimento externo do cambioschaco.
   *   2. Ultima cotacao conhecida (cotacaoAtual) — vinda do backend.
   *   3. Fallback seguro = 1 (nao-zero porque backend rejeita nulos e
   *      valores <= 0 nao fazem sentido economicamente).
   */
  private resolverCampoObrigatorio(campo: 'dolarGuarani' | 'realGuarani' | 'dolarReal'): number {
    const doForm = this.form[campo];
    if (doForm != null && !Number.isNaN(doForm) && doForm > 0) {
      return doForm;
    }
    const daCotacaoAtual = this.cotacaoAtual?.[campo];
    if (daCotacaoAtual != null && !Number.isNaN(daCotacaoAtual) && daCotacaoAtual > 0) {
      return daCotacaoAtual;
    }
    return 1;
  }

  private createEmptyForm(): CotacaoDTO {
    // A moeda padrao e preenchida dinamicamente a partir da sessao da empresa
    // (loadMoedaEmpresa). Enquanto carrega, permanece 'GUARANI' como fallback.
    return {
      moedaPadrao: (this.moedaEmpresa ?? 'GUARANI') as any,
      emissao: '',
    };
  }

  buscarCotacaoExterna(): void {
    this.cotacaoCarregando = true;

    fetch('https://www.cambioschaco.com.py/api/branch_office/27/exchange')
      .then(response => response.json())
      .then((data: any) => {
        const items = data.items || [];

        const usd = items.find((i: any) => i.isoCode === 'USD');
        const brl = items.find((i: any) => i.isoCode === 'BRL');
        const ars = items.find((i: any) => i.isoCode === 'ARS');

        if (this.isVisivel('dolarGuarani')) {
          this.form.dolarGuarani = usd?.purchasePrice || 7180;
        }
        if (this.isVisivel('realGuarani')) {
          this.form.realGuarani = brl?.purchasePrice || 1300;
        }
        if (this.isVisivel('dolarReal')) {
          this.form.dolarReal = brl?.purchaseArbitrage || 5.5;
        }
        if (this.isVisivel('pesoGuarani')) {
          // Para empresas PESO o campo e obrigatorio: usa fallback se
          // a API externa nao devolveu ARS.
          this.form.pesoGuarani = ars?.purchasePrice || CotacaoComponent.PESO_GUARANI_FALLBACK;
        }

        this.cotacaoCarregando = false;
      })
      .catch(() => {
        if (this.isVisivel('dolarGuarani')) this.form.dolarGuarani = 7180;
        if (this.isVisivel('realGuarani')) this.form.realGuarani = 1300;
        if (this.isVisivel('dolarReal')) this.form.dolarReal = 5.5;
        if (this.isVisivel('pesoGuarani')) this.form.pesoGuarani = CotacaoComponent.PESO_GUARANI_FALLBACK;
        this.cotacaoCarregando = false;
      });
  }

  loadCotacaoAtual(): void {
    this.cotacaoAtualCarregando = true;
    // Mantem o endpoint ancorado em GUARANI por backcompat — o backend
    // retorna a ultima cotacao do usuario logado e o frontend filtra
    // quais pares exibir no card "Cotacion Actual" via isVisivel(...).
    this.cotacaoService.getCotacaoAtual('GUARANI').subscribe({
      next: (cotacao) => {
        this.cotacaoAtual = cotacao;
        this.cotacaoAtualCarregando = false;
      },
      error: () => {
        this.cotacaoAtual = null;
        this.cotacaoAtualCarregando = false;
      },
    });
  }
}
